import axios from "axios";
import {useAuthStore} from "@/stores/auth";
import {Preferences} from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { refreshTokens, silentReAuth } from "@/plugins/authService";
import {emitter} from "@/main";
import {useTraceId} from "@/composables/useTraceId";
import router from "@/router";
import * as Sentry from "@sentry/vue";

const api_url = import.meta.env.VITE_API_URL

let pendingRequestCount = 0;

const jwtInterceptor = axios.create({
    baseURL: api_url + "/api/",
    timeout: 15000,
});

const { generateTraceParent, extractTraceId } = useTraceId();

jwtInterceptor.interceptors.request.use(async (config) => {
    pendingRequestCount++;
    config._requestStartTime = Date.now();
    const traceParent = generateTraceParent();
    config.headers['traceparent'] = traceParent;
    // We extract just the ID part (the middle 32 chars) so it's searchable
    const traceId = extractTraceId(traceParent);
    if (traceId) {
        // This sets the tag globally for the current session state.
        // If the app crashes 50ms later, this tag will be attached.
        Sentry.setTag("trace_id", traceId);

        // Optional: Leave a breadcrumb so you see the API call in the Sentry timeline
        Sentry.addBreadcrumb({
            category: "api",
            message: `Request to ${config.url}`,
            level: "info",
            data: { trace_id: traceId }
        });
    }

    const accessToken = await Preferences.get({ key: "access_token" }).then(r => { return r.value })
    if (accessToken == null) {
        return config;
    }
    if (accessToken === "demoaccessToken") {
        return config;
    }
    config.headers.Authorization = `bearer ${accessToken}`;
    return config;
});

jwtInterceptor.interceptors.response.use(
    (response) => {
        pendingRequestCount--;
        return response;
    },
    async (error) => {
        pendingRequestCount = Math.max(0, pendingRequestCount - 1);
        // work around for demo user not using a real token, thus will always be rejected
        if ((await Preferences.get({ key: "access_token" })).value === "demoaccessToken") {
            return Promise.reject(error);
        }

        // 4. Capture Trace ID (Even if response is undefined/offline)
        let traceRef = null;

        // A. Try getting it from the Server Response (Best Source)
        if (error.response?.headers && error.response.headers['x-trace-id']) {
            traceRef = error.response.headers['x-trace-id'];
        }
        // B. Fallback: Get it from the Request we sent (Client Source)
        else if (error.config?.headers?.traceparent) {
            traceRef = extractTraceId(error.config.headers.traceparent);
        }

        // Format the suffix string (e.g. " [Ref: abc12345]")
        const refSuffix = traceRef ? ` [Ref: ${traceRef.substring(0, 8)}]` : "";

        // Attach trace ID to the error object so it can be used by the caller
        error.traceId = traceRef;
        error.traceSuffix = refSuffix;

        // The response is undefined or null. Could be one of the case no internet is available.
        if (!error.response) {
            console.warn("JWT-INTER: Error response is not defined.")
            console.debug(error) //logging the error object to see what is going on these cases where the server was reachable.
            if (error.code === 'ECONNABORTED') {
                const duration_ms = Date.now() - (error.config?._requestStartTime ?? 0);
                // Only report as a real timeout if the request ran close to the configured threshold.
                // Firefox aborts in-flight requests on navigation with ECONNABORTED (not ERR_CANCELED),
                // producing false-positive timeout reports with very short durations.
                const isRealTimeout = duration_ms >= 14000 && !error.config?.signal?.aborted;
                if (isRealTimeout) {
                    emitter.emit('request-timeout', {
                        url: error.config?.url,
                        method: error.config?.method,
                        duration_ms,
                        pendingRequestCount,
                        traceId: traceRef,
                    });
                }
                return Promise.reject(error);
            } else {
                // Update the user notification for network errors
                emitter.emit('snackbar', {
                    'snackbarText': `⚠️ Network Error. check internet.${refSuffix}`,
                    'snackbarTimeout': 10000,
                });
            }
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        if (error.response.status === 401 && !originalRequest._retry) {
            const store = useAuthStore()
            originalRequest._retry = true;

            // If the user is already logged out (e.g. a prior handler already called
            // setLoginStatus(false) + rejectTokenRefresh), reject immediately to prevent
            // parallel 401 handlers from each starting a new refresh cycle.
            if (!store.getIsLoggedInState) {
                return Promise.reject(error);
            }

            // If a refresh is already in progress (by this interceptor OR by the router
            // guard), wait for it to complete and use the token it resolved with.
            // The token is passed directly through the promise — no Preferences bridge
            // read needed, which avoids the native Capacitor write-latency race.
            if (store.getTokenRefreshingInProgress) {
                try {
                    const newToken = await store.waitForTokenRefresh();
                    if (!newToken) return Promise.reject(error);
                    originalRequest.headers['Authorization'] = 'bearer ' + newToken;
                    return axios(originalRequest);
                } catch (refreshError) {
                    return Promise.reject(refreshError);
                }
            }

            store.setTokenRefreshing(true);
            console.debug("JWT-INTER: Token expired, trying to refresh")

            // 1. Attempt to get the refresh token
            let storedRefreshToken = await Preferences.get({ key: "refresh_token" }).then(r => r.value);
            if (!storedRefreshToken) {
                // Fallback: Capacitor Preferences stores as "CapacitorStorage.<key>" in browser localStorage
                storedRefreshToken = localStorage.getItem("CapacitorStorage.refresh_token");
            }

            console.debug("JWT-INTER: Refresh token found: ", storedRefreshToken ? "YES" : "NULL");

            // 2. Safety Check: If no refresh token, reject all waiters and logout immediately.
            if (!storedRefreshToken) {
                console.warn("JWT-INTER: No refresh token available. Redirecting to login.");
                store.rejectTokenRefresh(new Error("No refresh token"));
                store.setLoginStatus(false);
                await Preferences.remove({ key: 'access_token' });
                await Preferences.remove({ key: 'refresh_token' });
                router.push({ name: 'login-azure' });
                return Promise.reject(error);
            }

            // 3. Perform Refresh — with interaction_required escalation
            try {
                let tokenData;
                try {
                    tokenData = await refreshTokens(storedRefreshToken);
                    console.debug("JWT-INTER: token refreshed successfully")
                } catch (refreshError) {
                    if (refreshError?.type !== 'NEEDS_INTERACTIVE') throw refreshError;
                    // On web, GenericOAuth2.authenticate(prompt=none) uses window.open() which
                    // Edge blocks outside a user gesture — it always hangs 6 s then times out.
                    // Re-throw immediately so the isExpiredSession branch handles it without delay.
                    if (Capacitor.getPlatform() === 'web') throw refreshError;
                    // Azure requires interactive re-auth (50199 / interaction_required).
                    // First try a silent re-auth (prompt=none): if the user still has an
                    // active Azure AD browser session this silently issues new tokens with
                    // no UI disruption. Only fall through to the full interactive sheet when
                    // the silent attempt itself reports that interaction is required.
                    console.debug("JWT-INTER: interaction_required — attempting silent re-auth (prompt=none)");
                    try {
                        tokenData = await silentReAuth();
                        console.debug("JWT-INTER: silent re-auth succeeded, retrying original request");
                    } catch (silentError) {
                        // Both silent paths exhausted — unrecoverable without a user gesture.
                        // Fall through to finalError which redirects to the login page.
                        throw silentError;
                    }
                }
                // Resolve all waiters with the new token directly (Capacitor-safe: no Preferences.get needed)
                store.resolveTokenRefresh(tokenData.access_token);
                originalRequest.headers['Authorization'] = 'bearer ' + tokenData.access_token;
                return axios(originalRequest);
            } catch (finalError) {
                // setLoginStatus(false) BEFORE rejectTokenRefresh so any waiter that
                // unblocks immediately reads the correct logged-out state.
                store.setLoginStatus(false);
                store.rejectTokenRefresh(finalError);

                // authService throws two shapes: { reason } from refreshTokens and { error } from silentReAuth.
                const rawError = finalError?.reason ?? finalError?.error ?? finalError;
                const isExpiredSession = finalError?.type === 'NEEDS_INTERACTIVE' || finalError?.type === 'CANCELLED';
                if (isExpiredSession) {
                    emitter.emit('snackbar', {
                        snackbarText: `Your session has expired. Please log in again.${refSuffix}`,
                        snackbarTimeout: 6000,
                    });
                } else {
                    const sentryError = rawError instanceof Error
                        ? rawError
                        : new Error(`Token refresh failed (type: ${finalError?.type ?? 'unknown'})`);
                    Sentry.captureException(sentryError, {
                        tags: { auth_failure: 'token_refresh_failed' },
                        extra: { originalError: finalError },
                    });
                    emitter.emit('snackbar', {
                        snackbarText: `Authentication error. Please log in again.${refSuffix}`,
                        snackbarTimeout: 6000,
                    });
                }

                console.warn("JWT-INTER: Auth unrecoverable, redirecting to login", rawError);
                await Preferences.remove({ key: 'access_token' });
                await Preferences.remove({ key: 'refresh_token' });
                router.push({ name: 'login-azure' });
                return Promise.reject(finalError);
            }

        } else if (error.response.status === 403 && error.response.data?.errorCode === 'no_linked_account') {
            // Token is valid — retrying via refresh would fail identically. Don't retry.
            // Don't show the generic snackbar either — stores/user.ts owns this UX via accountLinkError.
            console.warn('JWT-INTER: authenticated identity has no linked PCP account');
            error._reported = true;
            return Promise.reject(error);
        } else if (error.response.status === 404) {
            // Trace info is now already attached above.
            // We reject 'error' (the full object) so the component catch() can use traceSuffix.
            console.debug("JWT-INTER: page not found: " + error.config.url)
            return Promise.reject(error);
        } else if (error.response.status === 503) {
            // Backend returned a controlled 503 (e.g. DB connection lost, upstream timeout).
            // If the caller marked the request as non-critical (profile pictures etc.),
            // suppress the snackbar entirely so the component handles it silently.
            console.warn(`JWT-INTER: 503 Service Unavailable on ${error.config?.url}`);
            if (!error.config?.headers?.['x-skip-error-snackbar']) {
                emitter.emit('snackbar', {
                    'snackbarText': `Service temporarily unavailable. Please try again shortly.${refSuffix}`,
                    'snackbarTimeout': 8000,
                });
            }
            error._reported = true;
            return Promise.reject(error);
        } else {
            // 5. Inject Trace ID into General Error Snackbars
            let message = '';

            if (error.response.data.detail) {
                message = error.response.data.detail;
                console.error(error.response.data.trace);
            } else if (error.response.data.error) {
                message = error.response.data.error;
            } else if (error.response.data.errors) {
                // If 'errors' is an array/object, you might want to stringify it or pick the first one
                message = JSON.stringify(error.response.data.errors);
            } else if (error.response.data.message) {
                message = error.response.data.message;
            } else {
                message = '‼️Problem with the server. Please try again later. Logs for details. ';
            }

            // Append the Ref ID
            const finalMessage = `${message}${refSuffix}`;

            // IMPORTANT: If we emit here, components that also have .catch() might emit TWICE.
            // We only emit here if the error hasn't been handled or if we want a global fallback.
            // However, the issue description says they want to add trace-id to snackbar text.
            // If the component already emits a snackbar, we should let it do so with the trace-id we attached.

            // Set a flag that this error was already reported via global snackbar
            error._reported = true;

            emitter.emit('snackbar', {
                'snackbarText': finalMessage,
                'error': error // Pass the error object so App.vue can use it if needed (though already appended)
            });

            console.error(`JWT-INTER: Network error, code: ${error.response.status} | Ref: ${traceRef || 'N/A'}`);
            return Promise.reject(error);
        }
    }
);

export default jwtInterceptor;
